import * as React from "react";
import { Laptop, Moon, Sun } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * Light / dark / system.
 *
 * The palette in src/index.css defines all three states at TOKEN level:
 *   :root                                    the light tokens
 *   @media (prefers-color-scheme: dark)
 *     :root:not([data-theme="light"])        system-dark
 *   :root[data-theme="dark"]                 explicit dark
 *
 * So this component's whole job is to stamp - or clear - data-theme on <html>.
 * "System" clears the attribute and lets the media query decide; it never
 * writes data-theme="light" and then hopes, which is what breaks the un-stamped
 * state.
 *
 * sp-migrate configures darkMode in its Tailwind config and never implements
 * it. This is the implementation.
 */

export type Theme = "light" | "dark" | "system";

const STORAGE_KEY = "spm365.theme";

function readStoredTheme(): Theme {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === "light" || raw === "dark" || raw === "system") return raw;
  } catch {
    // Private mode, blocked site data, a disabled storage API. The preference
    // is simply not remembered; nothing else about the app changes.
  }
  return "system";
}

function writeStoredTheme(theme: Theme): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // As above: a preference that could not be saved is not an error worth
    // interrupting anyone over.
  }
}

/** Stamps or clears data-theme on the document element. */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", theme);
}

export function useTheme(): [Theme, (theme: Theme) => void] {
  const [theme, setThemeState] = React.useState<Theme>(() => readStoredTheme());

  React.useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = React.useCallback((next: Theme) => {
    writeStoredTheme(next);
    setThemeState(next);
  }, []);

  return [theme, setTheme];
}

const LABELS: Record<Theme, string> = {
  light: "Light",
  dark: "Dark",
  system: "System",
};

export function ThemeToggle(): React.ReactElement {
  const [theme, setTheme] = useTheme();
  const Icon = theme === "dark" ? Moon : theme === "light" ? Sun : Laptop;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          data-testid="theme-toggle"
          aria-label={`Colour theme: ${LABELS[theme]}`}
        >
          <Icon aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Colour theme</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={theme}
          onValueChange={(value) => {
            if (value === "light" || value === "dark" || value === "system") setTheme(value);
          }}
        >
          <DropdownMenuRadioItem value="light" data-testid="theme-light">
            Light
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark" data-testid="theme-dark">
            Dark
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="system" data-testid="theme-system">
            System
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
